// Manual-corners perspective straighten. The user drags four corners over the
// photo; we warp the quad to an upright rectangle via a bilinear triangle mesh.
// (Honest scope: this is assisted-manual, not CV-grade auto edge detection.)

type Pt = { x: number; y: number };

export interface StraightenResult { blob: Blob; width: number; height: number; }

export function mountStraighten(
  container: HTMLElement,
  file: Blob,
  onDone: (r: StraightenResult) => void,
  onCancel: () => void
) {
  const url = URL.createObjectURL(file);
  const img = new Image();
  img.onload = () => start();
  img.src = url;

  function start() {
    container.hidden = false;
    container.innerHTML = '';

    const maxW = Math.min(container.clientWidth || 340, 360);
    const scale = maxW / img.naturalWidth;
    const dispW = Math.round(img.naturalWidth * scale);
    const dispH = Math.round(img.naturalHeight * scale);

    const wrap = document.createElement('div');
    wrap.style.cssText = `position:relative;border-radius:16px;overflow:hidden;background:#1a1517;width:${dispW}px;max-width:100%`;
    const canvas = document.createElement('canvas');
    canvas.width = dispW; canvas.height = dispH;
    canvas.style.cssText = 'display:block;width:100%';
    const ctx = canvas.getContext('2d')!;
    wrap.appendChild(canvas);

    // default corners: inset 8%
    const ins = 0.08;
    const corners: Pt[] = [
      { x: dispW * ins, y: dispH * ins },
      { x: dispW * (1 - ins), y: dispH * ins },
      { x: dispW * (1 - ins), y: dispH * (1 - ins) },
      { x: dispW * ins, y: dispH * (1 - ins) },
    ];

    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', `0 0 ${dispW} ${dispH}`);
    svg.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;pointer-events:none';
    const poly = document.createElementNS('http://www.w3.org/2000/svg', 'polygon');
    poly.setAttribute('fill', 'rgba(189,136,54,0.13)');
    poly.setAttribute('stroke', 'var(--accent, #b06a6f)');
    poly.setAttribute('stroke-width', '1.5');
    svg.appendChild(poly);
    wrap.appendChild(svg);

    const handles: HTMLElement[] = corners.map((_, i) => {
      const h = document.createElement('div');
      h.style.cssText = 'position:absolute;width:22px;height:22px;border-radius:999px;background:var(--accent,#b06a6f);border:2.5px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.4);transform:translate(-50%,-50%);touch-action:none;cursor:grab';
      h.addEventListener('pointerdown', (e) => drag(e, i));
      wrap.appendChild(h);
      return h;
    });

    function redraw() {
      ctx.drawImage(img, 0, 0, dispW, dispH);
      poly.setAttribute('points', corners.map((p) => `${p.x},${p.y}`).join(' '));
      corners.forEach((p, i) => { handles[i].style.left = p.x + 'px'; handles[i].style.top = p.y + 'px'; });
    }

    function drag(e: PointerEvent, i: number) {
      e.preventDefault();
      const move = (ev: PointerEvent) => {
        const rect = canvas.getBoundingClientRect();
        const sx = dispW / rect.width, sy = dispH / rect.height;
        corners[i] = {
          x: Math.max(0, Math.min(dispW, (ev.clientX - rect.left) * sx)),
          y: Math.max(0, Math.min(dispH, (ev.clientY - rect.top) * sy)),
        };
        redraw();
      };
      const up = () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); };
      window.addEventListener('pointermove', move);
      window.addEventListener('pointerup', up);
    }

    const tip = document.createElement('div');
    tip.style.cssText = 'position:absolute;top:10px;left:10px;display:inline-flex;align-items:center;gap:6px;background:rgba(0,0,0,.55);color:#fff;border-radius:999px;padding:5px 11px;font-size:12px;font-weight:600';
    tip.innerHTML = '<span style="width:7px;height:7px;border-radius:999px;background:var(--accent,#b06a6f)"></span> Drag the corners to the cards';
    wrap.appendChild(tip);

    const actions = document.createElement('div');
    actions.style.cssText = 'display:flex;gap:10px;margin-top:12px';
    const retake = btn('Retake', 'ghost');
    const apply = btn('Straighten & tidy', 'primary');
    apply.style.flex = '1';
    actions.appendChild(retake); actions.appendChild(apply);

    retake.addEventListener('click', () => { cleanup(); onCancel(); });
    apply.addEventListener('click', () => {
      const out = warp(img, corners, scale);
      out.toBlob((blob) => {
        if (blob) onDone({ blob, width: out.width, height: out.height });
      }, 'image/jpeg', 0.9);
    });

    container.appendChild(wrap);
    container.appendChild(actions);
    redraw();

    function cleanup() { URL.revokeObjectURL(url); container.innerHTML = ''; container.hidden = true; }
  }
}

function btn(label: string, kind: string): HTMLButtonElement {
  const b = document.createElement('button');
  b.type = 'button'; b.textContent = label; b.className = `btn ${kind} sm`;
  return b;
}

function bilerp(c: Pt[], u: number, v: number): Pt {
  // c = [TL, TR, BR, BL]
  const top = { x: c[0].x + (c[1].x - c[0].x) * u, y: c[0].y + (c[1].y - c[0].y) * u };
  const bot = { x: c[3].x + (c[2].x - c[3].x) * u, y: c[3].y + (c[2].y - c[3].y) * u };
  return { x: top.x + (bot.x - top.x) * v, y: top.y + (bot.y - top.y) * v };
}

// Warp the source quad (in display coords; scale maps display→image px) to an
// upright rectangle using a triangle mesh.
function warp(img: HTMLImageElement, dispCorners: Pt[], scale: number): HTMLCanvasElement {
  const c = dispCorners.map((p) => ({ x: p.x / scale, y: p.y / scale })); // image-space corners
  const dist = (a: Pt, b: Pt) => Math.hypot(a.x - b.x, a.y - b.y);
  const outW = Math.round((dist(c[0], c[1]) + dist(c[3], c[2])) / 2);
  const outH = Math.round((dist(c[0], c[3]) + dist(c[1], c[2])) / 2);
  const out = document.createElement('canvas');
  out.width = Math.max(1, outW); out.height = Math.max(1, outH);
  const ctx = out.getContext('2d')!;
  const N = 12;
  for (let i = 0; i < N; i++) {
    for (let j = 0; j < N; j++) {
      const u0 = i / N, u1 = (i + 1) / N, v0 = j / N, v1 = (j + 1) / N;
      const s00 = bilerp(c, u0, v0), s10 = bilerp(c, u1, v0), s11 = bilerp(c, u1, v1), s01 = bilerp(c, u0, v1);
      const d00 = { x: u0 * outW, y: v0 * outH }, d10 = { x: u1 * outW, y: v0 * outH };
      const d11 = { x: u1 * outW, y: v1 * outH }, d01 = { x: u0 * outW, y: v1 * outH };
      drawTri(ctx, img, [s00, s10, s11], [d00, d10, d11]);
      drawTri(ctx, img, [s00, s11, s01], [d00, d11, d01]);
    }
  }
  return out;
}

function drawTri(ctx: CanvasRenderingContext2D, img: HTMLImageElement, s: Pt[], d: Pt[]) {
  ctx.save();
  ctx.beginPath();
  ctx.moveTo(d[0].x, d[0].y); ctx.lineTo(d[1].x, d[1].y); ctx.lineTo(d[2].x, d[2].y); ctx.closePath();
  ctx.clip();
  // affine mapping src triangle → dest triangle
  const [x0, y0] = [s[0].x, s[0].y], [x1, y1] = [s[1].x, s[1].y], [x2, y2] = [s[2].x, s[2].y];
  const [u0, v0] = [d[0].x, d[0].y], [u1, v1] = [d[1].x, d[1].y], [u2, v2] = [d[2].x, d[2].y];
  const denom = x0 * (y1 - y2) - x1 * (y0 - y2) + x2 * (y0 - y1);
  if (Math.abs(denom) < 1e-6) { ctx.restore(); return; }
  const a = (u0 * (y1 - y2) - u1 * (y0 - y2) + u2 * (y0 - y1)) / denom;
  const b = (x0 * (u1 - u2) - x1 * (u0 - u2) + x2 * (u0 - u1)) / denom;
  const c2 = (x0 * (y1 * u2 - y2 * u1) - x1 * (y0 * u2 - y2 * u0) + x2 * (y0 * u1 - y1 * u0)) / denom;
  const dd = (v0 * (y1 - y2) - v1 * (y0 - y2) + v2 * (y0 - y1)) / denom;
  const e = (x0 * (v1 - v2) - x1 * (v0 - v2) + x2 * (v0 - v1)) / denom;
  const f = (x0 * (y1 * v2 - y2 * v1) - x1 * (y0 * v2 - y2 * v0) + x2 * (y0 * v1 - y1 * v0)) / denom;
  ctx.transform(a, dd, b, e, c2, f);
  ctx.drawImage(img, 0, 0);
  ctx.restore();
}
